import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { BookOpenIcon as BookOpen, PlusIcon as Plus, FloppyDiskIcon as FloppyDisk, TrashIcon as Trash, PencilIcon as Pencil, ArrowLeftIcon as ArrowLeft, LightningIcon as Lightning, ListChecksIcon as ListChecks, SparkleIcon as Sparkle } from "@phosphor-icons/react";

/**
 * Admin → Puppy school.
 *
 * Admin authors the 7-day drip course delivered to new buyers at day
 * 1/3/5/7/14/30 after they receive their dog (per the cron in
 * services.lifecycle_emails). Only `day` values in the scheduled set
 * are actually sent; others can sit in the collection as drafts for
 * future drip cadence experiments.
 *
 * Layout: left = lesson list table (sorted by day), right = editor
 * for the selected lesson. Mirrors the AdminJournal layout so the
 * mental model is identical.
 */
const blankForm = {
  id: null,
  day: 1,
  title: "",
  summary: "",
  body_html: "",
  active: true,
};

export default function AdminPuppySchool() {
  const [lessons, setLessons] = useState(null);
  const [scheduled, setScheduled] = useState([1, 3, 5, 7, 14, 30]);
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("list");
  const [forcing, setForcing] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    api.get("/admin/puppy-school/lessons")
      .then((r) => {
        setLessons(r.data.lessons || []);
        if (Array.isArray(r.data.scheduled_days)) setScheduled(r.data.scheduled_days);
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        setLessons([]);
      });
  };
  useEffect(load, []);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/puppy-school/seed-defaults");
      const created = data.created?.length || 0;
      const skipped = data.skipped_days?.length || 0;
      if (created > 0) {
        toast.success(
          `Seeded ${created} lesson${created === 1 ? "" : "s"}` +
          (skipped > 0 ? ` (skipped ${skipped} already active).` : "."),
        );
      } else {
        toast.info("All starter lessons already exist — nothing to seed.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  const startCreate = () => { setForm(blankForm); setView("edit"); };
  const startEdit = (l) => { setForm({ ...blankForm, ...l }); setView("edit"); };
  const cancel = () => { setForm(blankForm); setView("list"); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        day: Number(form.day),
        title: form.title.trim(),
        summary: form.summary.trim(),
        body_html: form.body_html,
      };
      if (form.id) {
        payload.active = !!form.active;
        const { data } = await api.patch(`/admin/puppy-school/lessons/${form.id}`, payload);
        toast.success("Lesson updated.");
        setForm({ ...blankForm, ...data });
      } else {
        const { data } = await api.post("/admin/puppy-school/lessons", payload);
        toast.success("Lesson created.");
        setForm({ ...blankForm, ...data });
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this lesson? It will stop sending to new owners but past sends are preserved.")) return;
    try {
      await api.delete(`/admin/puppy-school/lessons/${id}`);
      toast.success("Deactivated.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const forceRun = async () => {
    setForcing(true);
    try {
      const { data } = await api.post("/admin/lifecycle/run/puppy_school");
      toast.success(
        `Ran now → checked ${data.result.checked}, sent ${data.result.sent}` +
        (data.result.skipped_optout ? `, skipped ${data.result.skipped_optout}` : ""),
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setForcing(false);
    }
  };

  if (!lessons) return <AppShell><div className="max-w-5xl mx-auto px-4 py-10">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="admin-puppy-school">
        <header className="mb-6">
          <Link to="/admin" className="text-[12px] text-[#6B6B6B] hover:text-ink mb-2 inline-block">
            ← Back to admin
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-spectral text-2xl sm:text-3xl tracking-tight flex items-center gap-2">
                <BookOpen size={26} weight="duotone" /> Puppy school
              </h1>
              <p className="text-[13px] text-[#6B6B6B] mt-1 max-w-2xl">
                Authored drip course delivered to new owners at day{" "}
                <span className="font-mono text-ink">{scheduled.join(" / ")}</span>{" "}
                after a dog transfer. Only those days fire; other day numbers can sit as drafts.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={seedDefaults}
                disabled={seeding}
                className="rounded-full"
                data-testid="puppy-school-seed-defaults"
                title="Adds the 6 starter lessons (day 1, 3, 5, 7, 14, 30). Skips any day that's already active — fully idempotent. Every lesson is editable after seeding."
              >
                <Sparkle size={14} weight="bold" className="mr-1" />
                {seeding ? "Seeding…" : "Seed starter pack"}
              </Button>
              <Button
                variant="outline"
                onClick={forceRun}
                disabled={forcing}
                className="rounded-full"
                data-testid="puppy-school-force-run"
                title="Run the cron now — useful right after seeding lessons."
              >
                <Lightning size={14} weight="bold" className="mr-1" />
                {forcing ? "Running…" : "Run now"}
              </Button>
              {view === "list" ? (
                <Button onClick={startCreate} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="puppy-school-new">
                  <Plus size={14} weight="bold" className="mr-1" /> New lesson
                </Button>
              ) : (
                <Button variant="outline" onClick={cancel} className="rounded-full">
                  <ArrowLeft size={14} weight="bold" className="mr-1" /> Back to list
                </Button>
              )}
            </div>
          </div>
        </header>

        {view === "list" ? (
          <LessonList
            lessons={lessons}
            scheduled={scheduled}
            onEdit={startEdit}
            onDeactivate={deactivate}
            onSeedDefaults={seedDefaults}
            seeding={seeding}
          />
        ) : (
          <LessonEditor form={form} setForm={setForm} busy={busy} onSave={save} scheduled={scheduled} />
        )}
      </div>
    </AppShell>
  );
}

function LessonList({ lessons, scheduled, onEdit, onDeactivate, onSeedDefaults, seeding }) {
  if (lessons.length === 0) {
    return (
      <section className="rounded-2xl border-2 border-dashed border-[#EAEAEA] p-10 text-center" data-testid="puppy-school-empty">
        <ListChecks size={28} weight="duotone" className="text-[#9A9A9A] mx-auto mb-2" />
        <p className="text-[13px] text-[#6B6B6B] mb-4 max-w-md mx-auto">
          No lessons yet. Seed the <strong>6-lesson starter pack</strong> in one click — every lesson is fully editable afterwards. Or write your own from scratch.
        </p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <Button
            onClick={onSeedDefaults}
            disabled={seeding}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="puppy-school-empty-seed"
          >
            <Sparkle size={14} weight="bold" className="mr-1" />
            {seeding ? "Seeding…" : "Seed starter pack (6 lessons)"}
          </Button>
        </div>
        <p className="text-[10.5px] text-[#9A9A9A] mt-4">
          The pack covers day 1, 3, 5, 7, 14, and 30 after homecoming — homecoming basics, feeding rhythm, crate confidence, first-week recap, socialization, and the day-30 review request.
        </p>
      </section>
    );
  }
  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white overflow-hidden" data-testid="puppy-school-list">
      <table className="w-full text-[13px]">
        <thead className="bg-[#FAFAFA] text-[10.5px] uppercase tracking-wider text-[#6B6B6B]">
          <tr>
            <th className="text-left px-3 py-2 w-16">Day</th>
            <th className="text-left px-3 py-2">Title</th>
            <th className="text-left px-3 py-2 w-28">Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {lessons.map((l) => {
            const willSend = l.active && scheduled.includes(l.day);
            return (
              <tr key={l.id} className="border-t border-[#F1ECDD]" data-testid={`puppy-school-row-${l.day}`}>
                <td className="px-3 py-2.5 font-tabular font-bold text-ink">D{l.day}</td>
                <td className="px-3 py-2.5">
                  <div className="font-bold text-ink">{l.title}</div>
                  <div className="text-[11.5px] text-[#6B6B6B] line-clamp-1">{l.summary}</div>
                </td>
                <td className="px-3 py-2.5">
                  {willSend ? (
                    <span className="inline-flex text-[10.5px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-800 border-emerald-200">
                      Sending
                    </span>
                  ) : !l.active ? (
                    <span className="inline-flex text-[10.5px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 bg-[#F4F4F4] text-[#6B6B6B] border-[#E5E5E5]">
                      Inactive
                    </span>
                  ) : (
                    <span className="inline-flex text-[10.5px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 bg-[#FBF7EC] text-[#8a6a00] border-[#E5D08A]" title="Active but day not in scheduled set">
                      Draft day
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => onEdit(l)} className="rounded-full h-7 text-[11px]" data-testid={`puppy-school-edit-${l.day}`}>
                    <Pencil size={11} weight="bold" className="mr-1" /> Edit
                  </Button>
                  {l.active && (
                    <Button size="sm" variant="outline" onClick={() => onDeactivate(l.id)} className="rounded-full h-7 text-[11px] text-crimson border-crimson/40 hover:bg-crimson hover:text-white">
                      <Trash size={11} weight="bold" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function LessonEditor({ form, setForm, busy, onSave, scheduled }) {
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inScheduled = scheduled.includes(Number(form.day));
  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6 space-y-4" data-testid="puppy-school-editor">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Day after homecoming</Label>
          <Input
            type="number"
            min={1}
            max={60}
            value={form.day}
            onChange={(e) => upd("day", e.target.value)}
            data-testid="puppy-school-day"
            className="font-tabular"
          />
          <p className="text-[10.5px] mt-1">
            {inScheduled ? (
              <span className="text-emerald-700">✓ Sending day — drip fires automatically.</span>
            ) : (
              <span className="text-[#8a6a00]">⚠ Not in scheduled set ({scheduled.join("/")}). Will sit as a draft until you adjust the cron.</span>
            )}
          </p>
        </div>
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="puppy-school-title" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Summary <span className="text-[#9A9A9A]">(shown in inbox + email subject context)</span></Label>
        <Input value={form.summary} onChange={(e) => upd("summary", e.target.value)} data-testid="puppy-school-summary" />
      </div>

      <div>
        <Label className="text-xs">Body (HTML)</Label>
        <Textarea
          value={form.body_html}
          onChange={(e) => upd("body_html", e.target.value)}
          rows={14}
          className="font-mono text-[12.5px]"
          placeholder={`<p>Welcome home! Here's how to set up a quiet sleep zone:</p>\n<ul>\n  <li>Crate in a low-traffic corner</li>\n  <li>Soft blanket, no big toys for week 1</li>\n  <li>Lights down by 9pm</li>\n</ul>`}
          data-testid="puppy-school-body"
        />
        <p className="text-[10.5px] text-[#9A9A9A] mt-1">
          Plain HTML — Resend renders it inline in the email body. Keep it short; one screen of phone.
        </p>
      </div>

      {form.id && (
        <label className="flex items-center gap-2 text-[12.5px] text-ink">
          <input
            type="checkbox"
            checked={!!form.active}
            onChange={(e) => upd("active", e.target.checked)}
            data-testid="puppy-school-active"
            className="accent-ink"
          />
          Active (uncheck to stop sending without deleting)
        </label>
      )}

      <div className="flex flex-wrap gap-2 pt-2">
        <Button onClick={onSave} disabled={busy || !form.title || !form.summary || !form.body_html} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="puppy-school-save">
          <FloppyDisk size={14} weight="bold" className="mr-1" />
          {busy ? "Saving…" : form.id ? "Save changes" : "Create lesson"}
        </Button>
      </div>
    </section>
  );
}
